import { useMemo, useState, useEffect } from 'react'
import { ChevronDown, Trophy, TrendingDown, Target, Star, DollarSign } from 'lucide-react'
import { useGames, useGame, useMatchGames, usePresses, useCreatePress, useDeletePress } from '../hooks/useGames'
import { useAuth } from '../contexts/AuthContext'
import { useMatch } from '../hooks/useMatches'
import { useRealtimeScores } from '../hooks/useRealtimeScores'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { netScore } from '../utils/handicaps'
import { buildGameHandicapBreakdown, buildSideBetHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { computeRoundResults, extractTeamPoints, stablefordPoints } from '../utils/scoringEngine'
import { computeMatchSkinsMoney } from '../utils/sideBetEngine'
import RoundBanner from './RoundBanner'
import { useRoundResults } from '../hooks/useGameScoring'
import GameTypeBadge from './GameTypeBadge'
import { getMatchTypeLabels } from '../utils/matchTypeLabels'
import { hexToRgba, getInitials } from '../utils/colors'
import { computePotDistribution } from '../utils/payoutEngine'
import { useSideBets } from '../hooks/useSideBets'

export default function Leaderboard({ matchId, selectedSessionId, onClearSession, viewedGameId }) {
  const { data: games } = useGames()
  const { data: match } = useMatch(matchId)
  const labels = getMatchTypeLabels(match?.match_type)

  const isTeamMatch = match?.match_type === 'team_match' || match?.match_type === 'invitational'

  const matchGames = matchId
    ? games?.filter((g) => g.match_id === matchId) || []
    : games || []

  // Active or "entered" game — viewedGameId takes priority for non-team matches
  const activeGame = (!isTeamMatch && viewedGameId
    ? (match?.games || []).find(g => g.id === viewedGameId)
    : null
  ) || matchGames.find((g) => g.status === 'in_progress')

  // Local session ID — initialized from prop, managed internally for toggle behavior
  const [localSessionId, setLocalSessionId] = useState(selectedSessionId)
  useEffect(() => {
    if (selectedSessionId) setLocalSessionId(selectedSessionId)
  }, [selectedSessionId])

  const [view, setView] = useState(() => {
    try {
      const saved = localStorage.getItem(`press_leaders_view_${matchId}`)
      if (saved === 'match') return 'match'
    } catch { /* ignore */ }
    return 'round'
  })
  useEffect(() => {
    try { localStorage.setItem(`press_leaders_view_${matchId}`, view) } catch { /* ignore */ }
  }, [view, matchId])

  // If a specific session or game was selected (from prop), switch to round view
  useEffect(() => {
    if (selectedSessionId || viewedGameId) setView('round')
  }, [selectedSessionId, viewedGameId])

  // For team matches: find active session and its groups with linked games
  const activeSession = isTeamMatch
    ? (match?.sessions || []).find(s => s.status === 'in_progress')
    : null

  // Target session: prop-selected or local-selected completed session, or active session
  const targetSession = localSessionId
    ? (match?.sessions || []).find(s => s.id === localSessionId)
    : activeSession

  const sessionGroups = useMemo(() => {
    if (!targetSession) return []
    return (targetSession.session_groups || [])
      .slice()
      .sort((a, b) => a.group_order - b.group_order)
  }, [targetSession])

  // Find games for the target session (in_progress for active, completed for completed sessions)
  const sessionGames = useMemo(() => {
    if (!matchId || !targetSession) return []
    const allGames = match?.games || []
    const groupIds = new Set(sessionGroups.map(g => g.id))
    return allGames.filter(g => g.session_group_id && groupIds.has(g.session_group_id))
  }, [match?.games, targetSession, sessionGroups, matchId])

  // For team matches with session groups, match games to groups
  const groupGameMap = useMemo(() => {
    if (sessionGroups.length === 0 || sessionGames.length === 0) return {}
    const map = {}
    for (const group of sessionGroups) {
      const game = sessionGames.find(g => g.session_group_id === group.id)
        || (group.game_id ? sessionGames.find(g => g.id === group.game_id) : null)
      if (game) map[group.id] = game
    }
    return map
  }, [sessionGroups, sessionGames])

  // Only show toggle when a session is actively being viewed (active or entered via prop)
  const hasSessionData = isTeamMatch && (
    sessionGroups.length > 0 && Object.keys(groupGameMap).length > 0
  )

  // Matchup selector state (persisted in sessionStorage)
  const [selectedGroupId, setSelectedGroupId] = useState(() => {
    try { return sessionStorage.getItem(`leaderboard_matchup_${matchId}`) || null } catch { return null }
  })
  const handleSelectGroup = (groupId) => {
    setSelectedGroupId(groupId)
    try { sessionStorage.setItem(`leaderboard_matchup_${matchId}`, groupId) } catch {}
  }

  const effectiveGroupId = selectedGroupId && groupGameMap[selectedGroupId]
    ? selectedGroupId
    : sessionGroups.find(g => groupGameMap[g.id])?.id || null

  // Team match: show toggle between Session / Event when session data is available
  if (isTeamMatch && hasSessionData) {
    const hasSessionView = sessionGroups.length > 0 && Object.keys(groupGameMap).length > 0
    return (
      <div>
        {/* Toggle: Session / Event */}
        <div style={{
          display: 'flex',
          marginBottom: '1rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
          padding: '0.25rem',
        }}>
          <button
            onClick={() => setView('round')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: view === 'round' ? 'var(--bg-secondary)' : 'transparent',
              color: view === 'round' ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: view === 'round' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            Session
          </button>
          <button
            onClick={() => setView('match')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: view === 'match' ? 'var(--bg-secondary)' : 'transparent',
              color: view === 'match' ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: view === 'match' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            {labels.tabLabel}
          </button>
        </div>

        {view === 'round' && hasSessionView ? (
          <SessionLeaderboard
            matchId={matchId}
            sessionGroups={sessionGroups}
            groupGameMap={groupGameMap}
            selectedGroupId={effectiveGroupId}
            onSelectGroup={handleSelectGroup}
          />
        ) : view === 'round' && !hasSessionView ? (
          <SessionPicker
            sessions={(match?.sessions || []).filter(s => s.status === 'completed')}
            onSelectSession={(id) => setLocalSessionId(id)}
          />
        ) : (
          <EventStandings matchId={matchId} />
        )}
      </div>
    )
  }

  // Team match with no sessions at all — show event standings
  if (isTeamMatch) {
    return <EventStandings matchId={matchId} />
  }

  // Non-team match: original behavior
  if (!activeGame) {
    return <MatchTotals matchId={matchId} />
  }

  const isSingleRound = match?.match_type === 'quick_round'

  return (
    <div>
      {/* Toggle buttons — hidden for single rounds (no multi-round totals to show) */}
      {!isSingleRound && (
        <div style={{
          display: 'flex',
          marginBottom: '1rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
          padding: '0.25rem',
        }}>
          <button
            onClick={() => setView('round')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: view === 'round' ? 'var(--bg-secondary)' : 'transparent',
              color: view === 'round' ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: view === 'round' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            Round
          </button>
          <button
            onClick={() => setView('match')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: view === 'match' ? 'var(--bg-secondary)' : 'transparent',
              color: view === 'match' ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: view === 'match' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            {labels.tabLabel}
          </button>
        </div>
      )}

      {view === 'round' || isSingleRound
        ? <LeaderboardContent game={activeGame} matchId={matchId} />
        : <MatchTotals matchId={matchId} />
      }
    </div>
  )
}

/**
 * Picker shown when "Session" toggle is active but no specific session is selected.
 * Lists completed sessions for the user to drill into.
 */
function SessionPicker({ sessions, onSelectSession }) {
  if (sessions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        No completed sessions yet
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
        Select a session to view its leaderboard
      </p>
      {sessions
        .sort((a, b) => (a.session_order ?? 0) - (b.session_order ?? 0))
        .map(session => (
          <button
            key={session.id}
            onClick={() => onSelectSession(session.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div>
              <span style={{ fontWeight: '600', fontSize: '0.9375rem', color: 'var(--text-primary)', display: 'block' }}>
                {session.session_name || `Session ${(session.session_order ?? 0) + 1}`}
              </span>
              {session.course_name && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {session.course_name}
                </span>
              )}
            </div>
            <ChevronDown size={16} style={{ color: 'var(--text-muted)', transform: 'rotate(-90deg)' }} />
          </button>
        ))}
    </div>
  )
}

/**
 * Session leaderboard for team matches: matchup selector + per-matchup leaderboard
 */
function SessionLeaderboard({ matchId, sessionGroups, groupGameMap, selectedGroupId, onSelectGroup }) {
  const [showMatchupDropdown, setShowMatchupDropdown] = useState(false)
  const selectedGame = selectedGroupId ? groupGameMap[selectedGroupId] : null

  return (
    <div>
      {/* Matchup selector — same green dropdown as Scores page */}
      {sessionGroups.length > 1 && (
        <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
          <button
            onClick={() => setShowMatchupDropdown(!showMatchupDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: '100%',
              padding: '0.625rem',
              borderRadius: '9999px',
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              fontSize: '0.9375rem',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            {sessionGroups.find(g => g.id === selectedGroupId)?.group_name || 'Matchup'}
            <ChevronDown size={16} style={{ transition: 'transform 0.15s', transform: showMatchupDropdown ? 'rotate(180deg)' : 'none' }} />
          </button>
          {showMatchupDropdown && (
            <>
              <div onClick={() => setShowMatchupDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 0.25rem)',
                left: 0,
                right: 0,
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                zIndex: 100,
                overflow: 'hidden',
              }}>
                {sessionGroups.map(group => {
                  const isSelected = group.id === selectedGroupId
                  const groupGame = groupGameMap?.[group.id]
                  const playerCount = groupGame?.game_players?.length
                  return (
                    <button
                      key={group.id}
                      onClick={() => { onSelectGroup(group.id); setShowMatchupDropdown(false) }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        fontSize: '0.875rem',
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? '#10b981' : 'var(--text-primary)',
                        backgroundColor: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span>{group.group_name}</span>
                      {playerCount && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{playerCount} players</span>}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {selectedGame && <LeaderboardContent game={selectedGame} matchId={matchId} hideGameName />}
    </div>
  )
}

/**
 * Event standings for team match / invitational.
 * Aggregates nassau points across all matchup games in all sessions.
 */
function EventStandings({ matchId }) {
  const { data: match } = useMatch(matchId)
  const { data: matchGamesData } = useMatchGames(matchId)
  const labels = getMatchTypeLabels(match?.match_type)

  // Find session games — only keep the latest game per session_group_id
  // (re-activating a session creates new games, so older duplicates must be excluded)
  const sessionGames = useMemo(() => {
    const allSessionGames = (matchGamesData || []).filter(g =>
      g.session_group_id && (g.status === 'completed' || g.status === 'in_progress')
    )
    // Keep only the most recent game per session_group_id
    const latestByGroup = new Map()
    for (const g of allSessionGames) {
      const existing = latestByGroup.get(g.session_group_id)
      if (!existing || new Date(g.created_at) > new Date(existing.created_at)) {
        latestByGroup.set(g.session_group_id, g)
      }
    }
    return Array.from(latestByGroup.values())
  }, [matchGamesData])

  const matchTeams = match?.match_teams || []

  const { standings: sideGameStandings, activeSideGames: eventSideGames } = useSideGameStandings(matchId, sessionGames, match?.match_players)
  const [selectedEventView, setSelectedEventView] = useState('standings')

  const eventPillOptions = useMemo(() => {
    const opts = [{ value: 'standings', label: `${labels.tabLabel} Standings` }]
    for (const sg of eventSideGames) {
      const labelKey = { low_net: 'lowNetEntireLabel', low_gross: 'lowGrossEntireLabel', stableford: 'stablefordEntireLabel', skins: 'skinsEntireLabel', skins_carry: 'skinsEntireLabel' }[sg.bet_type]
      opts.push({
        value: sg.bet_type,
        label: labels[labelKey],
        icon: SIDE_GAME_CONFIG[sg.bet_type]?.icon,
      })
    }
    return opts
  }, [eventSideGames, labels])

  // Reset to standings if selected side game was removed
  useEffect(() => {
    if (selectedEventView !== 'standings' && !eventSideGames.some(sg => sg.bet_type === selectedEventView)) {
      setSelectedEventView('standings')
    }
  }, [eventSideGames, selectedEventView])

  // Compute team points across all session games
  const teamStandings = useMemo(() => {
    if (!sessionGames.length || !matchTeams.length || !match?.match_players?.length) return []

    // Initialize points per match_team
    const teamPoints = new Map()
    for (const mt of matchTeams) {
      teamPoints.set(mt.id, {
        matchTeam: mt,
        points: 0,
        matchupsPlayed: 0,
        sessionBreakdown: [],
      })
    }

    for (const game of sessionGames) {
      const gameTeams = game.teams || []
      if (gameTeams.length < 2) continue

      const gameType = game.game_type
      if (!gameType || gameType === 'stroke') continue

      // Build team structure for this game
      const teamStructure = gameTeams
        .sort((a, b) => a.team_order - b.team_order)
        .map(t => ({
          name: t.team_name,
          playerIds: (t.team_players || []).map(tp => tp.player_id),
        }))

      // Build handicap breakdown
      const course = getGameCourse(game)
      const tee = getGameTee(game)
      const handicapBreakdown = buildGameHandicapBreakdown({
        gameType,
        teamStructure,
        matchPlayers: match.match_players,
        gamePlayers: game.game_players,
        course,
        tee,
        mixedTees: getMixedTees(game),
      })

      // Compute round results (use snapshot for completed games)
      let results
      if (game.status === 'completed' && game.final_results?.roundResults) {
        results = game.final_results.roundResults
      } else {
        const scores = game.scores || []
        const numHoles = game.num_holes || 18
        const cfg = { ...(game.game_config || {}) }
        if (course) cfg.holePars = course.par
        results = computeRoundResults(gameType, teamStructure, scores, handicapBreakdown, numHoles, cfg)
      }

      if (!results) continue

      // Extract points from results (nassau, stableford, six_pack, or per-nine)
      const gamePoints = extractTeamPoints(results, teamStructure.length)

      if (!gamePoints) continue

      // Map game team indices to match_teams by team_name
      const sortedGameTeams = gameTeams.sort((a, b) => a.team_order - b.team_order)
      for (let i = 0; i < sortedGameTeams.length; i++) {
        const gameTeam = sortedGameTeams[i]
        const matchTeam = matchTeams.find(mt => mt.team_name === gameTeam.team_name)
        if (matchTeam && teamPoints.has(matchTeam.id)) {
          const entry = teamPoints.get(matchTeam.id)
          entry.points += gamePoints[i] || 0
          entry.matchupsPlayed++
        }
      }
    }

    return Array.from(teamPoints.values())
      .sort((a, b) => b.points - a.points)
  }, [sessionGames, matchTeams, match?.match_players])

  // Compute pot distribution for payout column
  const potDistribution = useMemo(() => {
    if (!match?.buy_in || !match?.payout_config?.splits?.length || !teamStandings.length) return null
    const totalPot = (match.match_players?.length || 0) * match.buy_in
    if (totalPot <= 0) return null
    return { distribution: computePotDistribution(teamStandings, match.payout_config, totalPot), totalPot }
  }, [match?.buy_in, match?.payout_config, match?.match_players?.length, teamStandings])

  // Count sessions by status
  const sessions = match?.sessions || []
  const completedSessionCount = sessions.filter(s => s.status === 'completed').length
  const activeSessionCount = sessions.filter(s => s.status === 'in_progress').length

  if (!matchTeams.length) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40vh',
        gap: '1.5rem',
        textAlign: 'center',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: 'var(--bg-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '2rem' }}>🏆</span>
        </div>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No Teams Yet
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Set up teams to see standings
          </p>
        </div>
      </div>
    )
  }

  if (!sessionGames.length) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40vh',
        gap: '1.5rem',
        textAlign: 'center',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: 'var(--bg-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '2rem' }}>📊</span>
        </div>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No Sessions Scored Yet
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Standings will appear once a session is started
          </p>
        </div>
      </div>
    )
  }

  const maxPoints = teamStandings.length > 0 ? Math.max(...teamStandings.map(t => t.points)) : 0
  const isMatchDecided = match?.status === 'completed'
  // Winner must be sole leader (not a tie)
  const leadersCount = teamStandings.filter(t => t.points === maxPoints).length
  const hasWinner = isMatchDecided && maxPoints > 0 && leadersCount === 1

  const selectedEventStanding = sideGameStandings.find(s => s.sideBet.bet_type === selectedEventView)
  const selectedEventLabel = eventPillOptions.find(o => o.value === selectedEventView)?.label

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>{labels.tabLabel} Standings</h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', display: 'block', marginTop: '0.125rem' }}>
          {completedSessionCount > 0 && `${completedSessionCount} completed session${completedSessionCount !== 1 ? 's' : ''}`}
          {completedSessionCount > 0 && activeSessionCount > 0 && ' · '}
          {activeSessionCount > 0 && `${activeSessionCount} session in progress`}
          {completedSessionCount === 0 && activeSessionCount === 0 && 'No sessions played yet'}
        </span>
      </div>

      <LeaderboardPillSelector
        selectedView={selectedEventView}
        onSelect={setSelectedEventView}
        options={eventPillOptions}
        labels={labels}
      />

      {selectedEventView === 'standings' ? (
        <>
          {/* Winner banner */}
          {hasWinner && (() => {
            const winnerName = teamStandings.find(t => t.points === maxPoints)?.matchTeam.team_name
            return (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                borderRadius: '0.75rem',
                backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.3)',
              }}>
                <Trophy size={18} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                <span style={{ fontWeight: '700', fontSize: '1rem', color: 'var(--accent-primary)' }}>
                  {winnerName} Wins!
                </span>
              </div>
            )
          })()}

          {/* Ryder Cup style side-by-side standings */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '0.75rem',
          }}>
            {teamStandings.map(({ matchTeam, points }, index) => {
              const isLeader = points === maxPoints && points > 0
              const teamPlayers = (matchTeam.match_team_players || [])
                .map(mtp => mtp.player)
                .filter(Boolean)

              return (
                <div
                  key={matchTeam.id}
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '0.75rem',
                    border: `1px solid ${isLeader ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.4)' : 'var(--border-color)'}`,
                    padding: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {/* Team name */}
                  <span style={{
                    fontWeight: '700',
                    fontSize: '1rem',
                    color: isLeader ? 'var(--accent-primary)' : 'var(--text-primary)',
                    textAlign: 'center',
                    marginBottom: '0.25rem',
                  }}>
                    {matchTeam.team_name}
                  </span>

                  {/* Points */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', lineHeight: 1.1 }}>
                    {hasWinner && isLeader && (
                      <Trophy size={20} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                    )}
                    <span style={{
                      fontWeight: '800',
                      fontSize: '2rem',
                      color: isLeader ? 'var(--accent-primary)' : 'var(--text-primary)',
                    }}>
                      {Number.isInteger(points) ? points : points.toFixed(1)}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.125rem' }}>
                    pts
                  </span>

                  {/* Player list */}
                  {teamPlayers.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.375rem',
                      marginTop: '0.625rem',
                      paddingTop: '0.625rem',
                      borderTop: '1px solid var(--border-color)',
                      width: '100%',
                    }}>
                      {teamPlayers.map(player => (
                        <div key={player.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '50%',
                            backgroundColor: player.avatar_color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '700',
                            fontSize: '0.5625rem',
                            color: '#fff',
                            flexShrink: 0,
                          }}>
                            {getInitials(player)}
                          </div>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                            {player.display_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      ) : selectedEventStanding ? (
        <SideGameStandingsCard standing={selectedEventStanding} label={selectedEventLabel} />
      ) : null}
    </div>
  )
}

/**
 * Computes side game standings from scorable games.
 * Returns { standings, activeSideGames } where standings is an array of
 * { sideBet, rows: [{ player, total, totalPar }], leaderValue }.
 */
function useSideGameStandings(matchId, scorableGames, matchPlayers) {
  const { data: sideBets } = useSideBets(matchId)

  const activeSideGames = useMemo(() => {
    if (!sideBets) return []
    return sideBets.filter(sb =>
      sb.bet_type === 'low_net' || sb.bet_type === 'low_gross' || sb.bet_type === 'stableford' ||
      sb.bet_type === 'skins' || sb.bet_type === 'skins_carry'
    )
  }, [sideBets])

  const roundsData = useMemo(() => {
    if (!scorableGames?.length || !matchPlayers?.length) return []
    return scorableGames.map((game) => {
      const course = getGameCourse(game)
      const tee = getGameTee(game)
      const gamePlayers = game.game_players || []
      const playerIds = gamePlayers.map((gp) => gp.player_id)
      const handicapBreakdown = buildSideBetHandicapBreakdown({
        matchPlayers, gamePlayers, course, tee, mixedTees: getMixedTees(game),
      })
      const holePars = course ? course.par.slice(0, game.num_holes || 18) : null
      return { scores: game.scores || [], handicapBreakdown, numHoles: game.num_holes || 18, playerIds, holePars }
    }).filter((r) => r.handicapBreakdown)
  }, [scorableGames, matchPlayers])

  const standings = useMemo(() => {
    if (!activeSideGames.length || !roundsData.length || !matchPlayers?.length) return []

    return activeSideGames.map((sideBet) => {
      const isSkins = sideBet.bet_type === 'skins' || sideBet.bet_type === 'skins_carry'

      // Skins: use computeMatchSkinsMoney to get skinsByPlayer
      if (isSkins) {
        const allPlayerIds = [...new Set(roundsData.flatMap(r => r.playerIds))]
        const carryOver = sideBet.bet_type === 'skins_carry'
        const grossTiebreaker = sideBet.config?.gross_tiebreaker ?? false
        const result = computeMatchSkinsMoney(roundsData, sideBet, allPlayerIds, carryOver, grossTiebreaker)

        const rows = []
        for (const pid of allPlayerIds) {
          const mp = matchPlayers.find((mp) => mp.player_id === pid)
          if (!mp?.player) continue
          const skinsWon = result.skinsByPlayer[pid] || 0
          rows.push({ player: mp.player, total: skinsWon, roundsPlayed: 0, totalPar: 0 })
        }
        if (rows.length === 0) return null
        rows.sort((a, b) => b.total - a.total)
        const leaderValue = rows[0]?.total
        if (leaderValue === 0) return null // no skins won yet
        return { sideBet, rows, leaderValue }
      }

      // Low Net / Low Gross / Stableford: accumulate per-hole scores
      const playerScores = new Map()

      for (const round of roundsData) {
        const { scores, handicapBreakdown, numHoles, playerIds, holePars } = round
        for (const pid of playerIds) {
          let value = 0
          let holesPlayed = 0
          for (let hole = 1; hole <= numHoles; hole++) {
            const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
            if (s?.strokes != null) {
              if (sideBet.bet_type === 'low_net') {
                const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
                const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
                value += s.strokes - strokesOnHole
              } else if (sideBet.bet_type === 'low_gross') {
                value += s.strokes
              } else if (sideBet.bet_type === 'stableford') {
                const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
                const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
                const net = s.strokes - strokesOnHole
                const par = holePars?.[hole - 1]
                const pts = stablefordPoints(net, par)
                if (pts != null) value += pts
              }
              holesPlayed++
            }
          }
          if (holesPlayed > 0) {
            if (!playerScores.has(pid)) {
              playerScores.set(pid, { total: 0, roundsPlayed: 0, totalPar: 0 })
            }
            const entry = playerScores.get(pid)
            entry.total += value
            entry.roundsPlayed++
            if (holePars) {
              entry.totalPar += holePars.slice(0, holesPlayed).reduce((s, p) => s + p, 0)
            }
          }
        }
      }

      const rows = []
      for (const [pid, data] of playerScores) {
        const mp = matchPlayers.find((mp) => mp.player_id === pid)
        if (!mp?.player) continue
        rows.push({
          player: mp.player,
          total: data.total,
          roundsPlayed: data.roundsPlayed,
          totalPar: data.totalPar,
        })
      }

      if (rows.length === 0) return null

      if (sideBet.bet_type === 'stableford') {
        rows.sort((a, b) => b.total - a.total)
      } else {
        rows.sort((a, b) => a.total - b.total)
      }

      const leaderValue = rows[0]?.total
      return { sideBet, rows, leaderValue }
    }).filter(Boolean)
  }, [activeSideGames, roundsData, matchPlayers])

  return { standings, activeSideGames }
}

const SIDE_GAME_CONFIG = {
  low_net: { icon: TrendingDown, showToPar: true },
  low_gross: { icon: Target, showToPar: true },
  stableford: { icon: Star, showToPar: false },
  skins: { icon: DollarSign, showToPar: false, columnLabel: 'Skins' },
  skins_carry: { icon: DollarSign, showToPar: false, columnLabel: 'Skins' },
}

function SideGameStandingsCard({ standing, label }) {
  const { sideBet, rows, leaderValue } = standing
  const config = SIDE_GAME_CONFIG[sideBet.bet_type]
  if (!config) return null
  const Icon = config.icon
  const formatToPar = (val) => val === 0 ? 'E' : val > 0 ? `+${val}` : val

  return (
    <div>
      <h3 style={{
        fontSize: '1rem',
        fontWeight: '700',
        color: 'var(--text-primary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '0.5rem',
      }}>
        {label}
      </h3>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Icon size={16} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Player
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.5rem', textAlign: 'right' }}>
              {config.columnLabel || (config.showToPar ? 'Score' : 'Pts')}
            </span>
            {config.showToPar && (
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
                +/–
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {rows.map(({ player, total, totalPar: playerPar }, index) => {
            const isLeader = total === leaderValue
            const toPar = total - playerPar

            return (
              <div
                key={player.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 0',
                  borderBottom: index < rows.length - 1 ? '1px solid var(--border-color)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', width: '1.25rem' }}>
                    {index + 1}.
                  </span>
                  <span style={{
                    fontWeight: isLeader ? '700' : '600',
                    fontSize: '1.0625rem',
                    color: isLeader ? 'var(--accent-primary)' : 'var(--text-primary)',
                  }}>
                    {player.display_name}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    fontSize: '0.9375rem',
                    color: isLeader ? 'var(--accent-primary)' : 'var(--text-muted)',
                    width: '2.5rem',
                    textAlign: 'right',
                  }}>
                    {total}
                  </span>
                  {config.showToPar && (
                    <span style={{
                      fontWeight: '800',
                      fontSize: '1.25rem',
                      width: '2.25rem',
                      textAlign: 'right',
                      color: isLeader ? 'var(--accent-primary)' : (toPar === 0 ? 'var(--text-muted)' : toPar < 0 ? 'var(--accent-primary)' : 'var(--accent-danger)'),
                    }}>
                      {formatToPar(toPar)}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** Green pill dropdown for switching between main standings and side game standings. */
function LeaderboardPillSelector({ selectedView, onSelect, options, labels }) {
  const [showDropdown, setShowDropdown] = useState(false)
  const selectedOption = options.find(o => o.value === selectedView)

  // No side games — don't render the pill at all
  if (options.length <= 1) return null

  return (
    <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          width: '100%',
          padding: '0.625rem',
          borderRadius: '9999px',
          backgroundColor: '#10b981',
          color: '#fff',
          border: 'none',
          fontSize: '0.9375rem',
          fontWeight: '700',
          cursor: 'pointer',
        }}
      >
        {selectedOption?.label || labels.singular}
        <ChevronDown size={16} style={{ transition: 'transform 0.15s', transform: showDropdown ? 'rotate(180deg)' : 'none' }} />
      </button>
      {showDropdown && (
        <>
          <div onClick={() => setShowDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            left: 0,
            right: 0,
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '0.5rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 100,
            overflow: 'hidden',
          }}>
            {options.map(option => {
              const isSelected = option.value === selectedView
              return (
                <button
                  key={option.value}
                  onClick={() => { onSelect(option.value); setShowDropdown(false) }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? '700' : '500',
                    color: isSelected ? '#10b981' : 'var(--text-primary)',
                    backgroundColor: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {option.icon && <option.icon size={16} style={{ color: isSelected ? '#10b981' : 'var(--text-muted)', flexShrink: 0 }} />}
                  <span>{option.label}</span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function MatchTotals({ matchId }) {
  const { data: matchGamesData } = useMatchGames(matchId)
  const { data: match } = useMatch(matchId)
  const labels = getMatchTypeLabels(match?.match_type)

  // Include both completed and in-progress games for live match totals
  const scorableGames = useMemo(
    () => (matchGamesData || []).filter((g) => g.status === 'completed' || g.status === 'in_progress'),
    [matchGamesData]
  )

  // Aggregate individual gross/net totals across all rounds (completed + in-progress)
  const playerTotals = useMemo(() => {
    if (!scorableGames.length || !match?.match_players?.length) return []

    const totals = new Map() // playerId -> { player, grossTotal, netTotal, grossToPar, netToPar, roundsPlayed }

    for (const game of scorableGames) {
      const scores = game.scores || []
      const players = game.game_players || []
      const course = getGameCourse(game)
      const tee = getGameTee(game)
      const totalPar = course ? course.par.reduce((s, p) => s + p, 0) : null

      // Build full course handicap breakdown (no game-type %, no off-low) for individual net scores
      const hcpBreakdown = buildSideBetHandicapBreakdown({
        matchPlayers: match.match_players, gamePlayers: players, course, tee, mixedTees: getMixedTees(game),
      })

      for (const { player_id, player } of players) {
        if (!player) continue
        const playerScores = scores.filter((s) => s.player_id === player_id && s.strokes != null)
        if (playerScores.length === 0) continue

        const gross = playerScores.reduce((sum, s) => sum + s.strokes, 0)
        const parPlayed = course
          ? playerScores.reduce((sum, s) => sum + course.par[s.hole_number - 1], 0)
          : null

        let net = null
        if (hcpBreakdown) {
          const hcp = hcpBreakdown.find((h) => h.playerId === player_id)
          if (hcp) {
            net = playerScores.reduce((sum, s) => {
              const strk = hcp.strokeHoles.get(s.hole_number) || 0
              return sum + netScore(s.strokes, strk)
            }, 0)
          }
        }

        if (!totals.has(player_id)) {
          const mp = match.match_players.find((m) => m.player_id === player_id)
          totals.set(player_id, {
            player,
            handicapIndex: mp ? Number(mp.handicap_index) : null,
            grossTotal: 0,
            netTotal: 0,
            grossToPar: 0,
            netToPar: 0,
            hasNet: false,
            roundsPlayed: 0,
          })
        }
        const entry = totals.get(player_id)
        entry.grossTotal += gross
        entry.roundsPlayed += 1
        if (parPlayed != null) {
          entry.grossToPar += gross - parPlayed
        }
        if (net != null && parPlayed != null) {
          entry.netTotal += net
          entry.netToPar += net - parPlayed
          entry.hasNet = true
        }
      }
    }

    return Array.from(totals.values()).sort((a, b) => {
      const aVal = a.hasNet ? a.netToPar : a.grossToPar
      const bVal = b.hasNet ? b.netToPar : b.grossToPar
      return aVal - bVal
    })
  }, [scorableGames, match?.match_players])

  // Compute total holes played / total holes across all rounds
  const { holesPlayed: totalHolesPlayed, totalHoles: totalHolesExpected } = useMemo(() => {
    let played = 0
    let total = 0
    for (const game of scorableGames) {
      total += game.num_holes || 18
      // Use the max holes any single player has scored as the "holes played" for this round
      const scores = game.scores || []
      const players = game.game_players || []
      if (players.length > 0 && scores.length > 0) {
        const maxHoles = Math.max(...players.map(({ player_id }) =>
          scores.filter((s) => s.player_id === player_id && s.strokes != null).length
        ))
        played += maxHoles
      }
    }
    return { holesPlayed: played, totalHoles: total }
  }, [scorableGames])

  const { standings, activeSideGames } = useSideGameStandings(matchId, scorableGames, match?.match_players)

  const [selectedView, setSelectedView] = useState('totals')

  // Build pill options: main totals + active side games
  const pillOptions = useMemo(() => {
    const opts = [{ value: 'totals', label: `${labels.singular} Total Scores` }]
    for (const sg of activeSideGames) {
      const labelKey = { low_net: 'lowNetEntireLabel', low_gross: 'lowGrossEntireLabel', stableford: 'stablefordEntireLabel', skins: 'skinsEntireLabel', skins_carry: 'skinsEntireLabel' }[sg.bet_type]
      opts.push({
        value: sg.bet_type,
        label: labels[labelKey],
        icon: SIDE_GAME_CONFIG[sg.bet_type]?.icon,
      })
    }
    return opts
  }, [activeSideGames, labels])

  // Reset to totals if selected side game was removed
  useEffect(() => {
    if (selectedView !== 'totals' && !activeSideGames.some(sg => sg.bet_type === selectedView)) {
      setSelectedView('totals')
    }
  }, [activeSideGames, selectedView])

  const selectedStanding = standings.find(s => s.sideBet.bet_type === selectedView)
  const selectedLabel = pillOptions.find(o => o.value === selectedView)?.label

  if (!scorableGames.length) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40vh',
        gap: '1.5rem',
        textAlign: 'center',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: 'var(--bg-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '2rem' }}>📊</span>
        </div>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No Rounds Yet
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Leaderboard will appear once a round is started
          </p>
        </div>
      </div>
    )
  }

  const formatToPar = (val) => val === 0 ? 'E' : val > 0 ? `+${val}` : val
  const toParColor = (val) => val === 0 ? 'var(--text-muted)' : val < 0 ? 'var(--accent-primary)' : 'var(--accent-danger)'

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>{labels.singular} Totals</h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', display: 'block', marginTop: '0.125rem' }}>
          {scorableGames.length} round{scorableGames.length !== 1 ? 's' : ''}
          {totalHolesPlayed > 0 && ` \u00B7 thru ${totalHolesPlayed} of ${totalHolesExpected} holes`}
        </span>
      </div>

      <LeaderboardPillSelector
        selectedView={selectedView}
        onSelect={setSelectedView}
        options={pillOptions}
        labels={labels}
      />

      {selectedView === 'totals' ? (
        <>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem',
          }}>
            {labels.singular} Total Scores
          </h3>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.75rem',
            border: '1px solid var(--border-color)',
            padding: '1.25rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Player
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
                  Gross
                </span>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2rem', textAlign: 'right' }}>
                  +/–
                </span>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
                  Net
                </span>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
                  +/–
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {playerTotals.map(({ player, grossTotal, netTotal, grossToPar, netToPar, hasNet, handicapIndex }, index) => (
                <div
                  key={player.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 0',
                    borderBottom: index < playerTotals.length - 1 ? '1px solid var(--border-color)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', width: '1.25rem' }}>
                      {index + 1}.
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '1.0625rem' }}>
                      {player.display_name}
                    </span>
                    {handicapIndex != null && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.125rem' }}>
                        ({handicapIndex})
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.9375rem', color: 'var(--text-muted)', width: '2.25rem', textAlign: 'right' }}>
                      {grossTotal || '–'}
                    </span>
                    <span style={{ fontSize: '0.9375rem', fontWeight: '600', width: '2rem', textAlign: 'right', color: toParColor(grossToPar) }}>
                      {formatToPar(grossToPar)}
                    </span>
                    <span style={{ fontSize: '0.9375rem', color: 'var(--text-muted)', width: '2.25rem', textAlign: 'right' }}>
                      {hasNet ? netTotal || '–' : '–'}
                    </span>
                    <span style={{ fontWeight: '800', fontSize: '1.25rem', width: '2.25rem', textAlign: 'right', color: hasNet ? toParColor(netToPar) : 'var(--text-muted)' }}>
                      {hasNet ? formatToPar(netToPar) : '–'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : selectedStanding ? (
        <SideGameStandingsCard standing={selectedStanding} label={selectedLabel} />
      ) : null}
    </div>
  )
}

function LeaderboardContent({ game, matchId, hideGameName }) {
  const { user } = useAuth()
  const { data: fullGame } = useGame(game.id)
  const { data: match } = useMatch(matchId)
  const { data: presses } = usePresses(game.id)
  const createPressMutation = useCreatePress()
  const deletePressMutation = useDeletePress()

  useRealtimeScores(game.id)

  const gameData = fullGame || game
  const players = gameData.game_players || []
  const scores = gameData.scores || []
  const numHoles = gameData.num_holes || 18

  const course = getGameCourse(gameData)
  const tee = getGameTee(gameData)
  const totalPar = course ? course.par.reduce((s, p) => s + p, 0) : null

  const handicapBreakdown = useMemo(() => {
    const gameTeams = gameData.teams || []
    const teamStructure = gameTeams
      .sort((a, b) => a.team_order - b.team_order)
      .map((t) => ({ playerIds: (t.team_players || []).map((tp) => tp.player_id) }))
    return buildGameHandicapBreakdown({
      gameType: gameData.game_type, teamStructure, matchPlayers: match?.match_players,
      gamePlayers: players, course, tee, mixedTees: getMixedTees(gameData),
    })
  }, [course, tee, match?.match_players, players, totalPar, gameData.game_type, gameData.teams])

  // Full course handicap breakdown (no game-type %, no off-low adjustment) for individual net scores
  const fullHandicapBreakdown = useMemo(() => {
    return buildSideBetHandicapBreakdown({
      matchPlayers: match?.match_players, gamePlayers: players, course, tee, mixedTees: getMixedTees(gameData),
    })
  }, [course, tee, match?.match_players, players, totalPar])

  const isOwner = gameData.created_by === user?.id || match?.owner_id === user?.id
  const isScorer = match?.match_players?.some(
    (mp) => mp.user_id === user?.id && mp.is_scorer
  )
  const isGameScorer = gameData.game_players?.some(
    (gp) => gp.player?.user_id === user?.id && gp.is_scorer
  )
  const isTeamEvent = match?.match_type === 'team_match' || match?.match_type === 'invitational'
  const canEditScores = isTeamEvent
    ? isGameScorer
    : (isOwner || isScorer || !matchId)

  const { results: roundResults } = useRoundResults(gameData, match?.match_players)

  const getStrokesOnHole = (playerId, hole) => {
    const hcp = handicapBreakdown?.find((h) => h.playerId === playerId)
    if (!hcp) return 0
    return hcp.strokeHoles.get(hole) || 0
  }

  const getTotal = (playerId) => {
    return scores
      .filter((sc) => sc.player_id === playerId && sc.strokes != null)
      .reduce((sum, sc) => sum + sc.strokes, 0)
  }

  const getNetTotal = (playerId) => {
    if (!handicapBreakdown) return null
    return scores
      .filter((sc) => sc.player_id === playerId && sc.strokes != null)
      .reduce((sum, sc) => {
        const strk = getStrokesOnHole(playerId, sc.hole_number)
        return sum + netScore(sc.strokes, strk)
      }, 0)
  }

  // Full-handicap net total for individual scores (no game-type %, no off-low)
  const getFullNetTotal = (playerId) => {
    if (!fullHandicapBreakdown) return null
    const hcp = fullHandicapBreakdown.find((h) => h.playerId === playerId)
    if (!hcp) return null
    return scores
      .filter((sc) => sc.player_id === playerId && sc.strokes != null)
      .reduce((sum, sc) => {
        const strk = hcp.strokeHoles.get(sc.hole_number) || 0
        return sum + netScore(sc.strokes, strk)
      }, 0)
  }

  const getPlayerCourseHandicap = (playerId) => {
    const hcp = fullHandicapBreakdown?.find((h) => h.playerId === playerId)
    return hcp?.courseHandicap ?? null
  }

  const handlePress = (dormieInfo) => {
    if (!canEditScores) return
    const segStart = dormieInfo.segment === 'back' ? 10 : 1
    const segEnd = dormieInfo.segment === 'front' ? 9 : numHoles

    let pressStartHole = 1
    for (let h = segStart; h <= segEnd; h++) {
      const allScored = players.every(({ player }) =>
        scores.some((s) => s.player_id === player?.id && s.hole_number === h && s.strokes != null)
      )
      if (!allScored) {
        pressStartHole = h
        break
      }
    }

    createPressMutation.mutate({
      game_id: game.id,
      segment: dormieInfo.segment,
      start_hole: pressStartHole,
      end_hole: segEnd,
      wager: Number(gameData.wager) || 0,
      pressing_team: dormieInfo.losingTeam,
    })
  }

  return (
    <div>
      {/* Game header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
      }}>
        <div>
          {!hideGameName && (
            <h2 style={{ fontSize: '1rem', fontWeight: '700' }}>
              {gameData.game_name || gameData.course_name || 'Current Round'}
            </h2>
          )}
          {gameData.course_name && (
            <span style={{ color: 'var(--text-secondary)', fontSize: hideGameName ? '1rem' : '0.8125rem', fontWeight: hideGameName ? '700' : '400', marginTop: '0.125rem', display: 'block' }}>
              {gameData.course_name}
            </span>
          )}
          {tee && (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: '0.125rem', display: 'block' }}>
              {tee.name} &middot; {tee.totalYardage?.toLocaleString()} yds &middot; Slope {tee.slope}
            </span>
          )}
          {gameData.game_type && gameData.game_type !== 'stroke' && (
            <div style={{ marginTop: '0.375rem' }}>
              <GameTypeBadge gameTypeId={gameData.game_type} />
            </div>
          )}
        </div>
      </div>

      {/* Round banner — team standings with press support */}
      <RoundBanner
        gameData={gameData}
        matchPlayers={match?.match_players}
        presses={presses}
      />

      {/* Divider */}
      <hr style={{
        border: 'none',
        borderTop: '1px solid var(--border-color)',
        margin: '0.75rem 0',
      }} />

      {/* Individual totals */}
      <h3 style={{
        fontSize: '1rem',
        fontWeight: '700',
        color: 'var(--text-primary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '0.5rem',
      }}>
        Round Individual Scores
      </h3>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Player
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2rem', textAlign: 'right' }}>
              +/–
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
              Gross
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2rem', textAlign: 'right' }}>
              +/–
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
              Net
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {players
            .map(({ player }) => player ? {
              player,
              total: getTotal(player.id),
              netTotal: getFullNetTotal(player.id),
            } : null)
            .filter(Boolean)
            .sort((a, b) => {
              const aVal = a.netTotal ?? a.total ?? 999
              const bVal = b.netTotal ?? b.total ?? 999
              return aVal - bVal
            })
            .map(({ player, total, netTotal }, index) => {
              const holesPlayed = scores.filter((sc) => sc.player_id === player.id && sc.strokes != null).length
              const parThrough = course && holesPlayed > 0
                ? scores
                    .filter((sc) => sc.player_id === player.id && sc.strokes != null)
                    .reduce((sum, sc) => sum + course.par[sc.hole_number - 1], 0)
                : null
              const grossToPar = parThrough != null && total > 0 ? total - parThrough : null
              const netToPar = parThrough != null && netTotal != null && netTotal > 0 ? netTotal - parThrough : null
              const courseHcp = getPlayerCourseHandicap(player.id)

              const formatToPar = (val) => val === 0 ? 'E' : val > 0 ? `+${val}` : val
              const toParColor = (val) => val === 0 ? 'var(--text-muted)' : val < 0 ? 'var(--accent-primary)' : 'var(--accent-danger)'

              return (
                <div
                  key={player.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 0',
                    borderBottom: index < players.length - 1 ? '1px solid var(--border-color)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', width: '1.25rem' }}>
                      {index + 1}.
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '1.0625rem' }}>
                      {player.display_name}
                    </span>
                    {courseHcp != null && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.125rem' }}>
                        ({courseHcp})
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.9375rem', fontWeight: '600', width: '2rem', textAlign: 'right', color: grossToPar != null ? toParColor(grossToPar) : 'var(--text-muted)' }}>
                      {grossToPar != null ? formatToPar(grossToPar) : ''}
                    </span>
                    <span style={{ fontSize: '0.9375rem', color: 'var(--text-muted)', width: '2.25rem', textAlign: 'right' }}>
                      {total || '–'}
                    </span>
                    <span style={{ fontSize: '0.9375rem', fontWeight: '600', width: '2rem', textAlign: 'right', color: netToPar != null ? toParColor(netToPar) : 'var(--text-muted)' }}>
                      {netToPar != null ? formatToPar(netToPar) : ''}
                    </span>
                    <span style={{ fontWeight: '800', fontSize: '1.25rem', width: '2.25rem', textAlign: 'right', color: netTotal != null && netTotal > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {(netTotal ?? total) || '–'}
                    </span>
                  </div>
                </div>
              )
            })}
        </div>
      </div>

    </div>
  )
}
